import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, of } from 'rxjs';
import { PlanAdminService } from '../../../core/services/plan-admin.service';

@Component({
  selector: 'app-plan-form',
  standalone: true,
  imports: [CommonModule, RouterLink, ReactiveFormsModule, InputTextModule, InputNumberModule,
    ButtonModule, ToggleSwitchModule, ProgressSpinnerModule, DividerModule, TooltipModule],
  templateUrl: './plan-form.html',
  styleUrl: './plan-form.scss'
})
export class PlanFormComponent implements OnInit {
  private fb     = inject(FormBuilder);
  private svc    = inject(PlanAdminService);
  private router = inject(Router);
  private route  = inject(ActivatedRoute);

  planId   = signal<string | null>(null);
  isEdit   = signal(false);
  loading  = signal(false);
  saving   = signal(false);
  error    = signal('');

  form = this.fb.group({
    code:               ['', Validators.required],
    name:               ['', Validators.required],
    priceMonthly:       [0, [Validators.required, Validators.min(0)]],
    maxProperties:      [3, Validators.required],
    maxPhotos:          [5, Validators.required],
    maxVideos:          [0, Validators.required],
    validityDays:       [30, [Validators.required, Validators.min(1)]],
    featured:           [false],
    virtualTourEnabled: [false],
    maxPanoramas:       [0, Validators.required],
    features:           this.fb.array([this.fb.control('')])
  });

  get featuresArray() { return this.form.get('features') as FormArray; }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.planId.set(id);
      this.isEdit.set(true);
      this.loading.set(true);
      this.svc.getPlans().pipe(catchError(() => of([]))).subscribe(plans => {
        const plan = plans.find(p => p.id === id);
        if (plan) {
          this.form.patchValue({
            code: plan.code, name: plan.name, priceMonthly: plan.priceMonthly,
            maxProperties: plan.maxProperties, maxPhotos: plan.maxPhotos, maxVideos: plan.maxVideos ?? 0,
            validityDays: plan.validityDays, featured: plan.featured,
            virtualTourEnabled: plan.virtualTourEnabled ?? false, maxPanoramas: plan.maxPanoramas ?? 0
          });
          this.form.get('code')?.disable();
          const fa = this.featuresArray;
          fa.clear();
          plan.features?.forEach(f => fa.push(this.fb.control(f)));
          if (fa.length === 0) fa.push(this.fb.control(''));
        }
        this.loading.set(false);
      });
    }
  }

  addFeature() { this.featuresArray.push(this.fb.control('')); }
  removeFeature(i: number) { if (this.featuresArray.length > 1) this.featuresArray.removeAt(i); }

  save() {
    if (!this.form.valid) return;
    this.saving.set(true);
    this.error.set('');

    const v = this.form.getRawValue();
    const data = {
      code:               v.code ?? '',
      name:               v.name ?? '',
      priceMonthly:       v.priceMonthly ?? 0,
      maxProperties:      v.maxProperties ?? 3,
      maxPhotos:          v.maxPhotos ?? 5,
      maxVideos:          v.maxVideos ?? 0,
      validityDays:       v.validityDays ?? 30,
      featured:           v.featured ?? false,
      virtualTourEnabled: v.virtualTourEnabled ?? false,
      maxPanoramas:       v.maxPanoramas ?? 0,
      features:           (v.features as string[]).filter(f => f.trim())
    };

    const req$ = this.isEdit()
      ? this.svc.updatePlan(this.planId()!, data)
      : this.svc.createPlan(data);

    req$.pipe(catchError(err => {
      this.error.set(err?.error?.message ?? 'Erreur lors de la sauvegarde.');
      this.saving.set(false);
      return of(null);
    })).subscribe(res => {
      if (res) this.router.navigate(['/plans']);
      else this.saving.set(false);
    });
  }
}
